/**
 * Pontos Anatômicos e Coordenadas de Setas HUD — Honda ADV 150 (Projeto K0W)
 * Padrão Concessionária Honda Dream & Filosofia Fábio Akita
 */

export interface HotspotCoordinate {
  x: number; // Percentual normalizado no eixo X (0.0 a 100.0%)
  y: number; // Percentual normalizado no eixo Y (0.0 a 100.0%)
}

export interface MotorcycleAnatomyHotspot {
  id: string;
  code: string;
  name: string;
  subsystem:
    | "motor"
    | "cvt"
    | "pgmfi"
    | "chassi"
    | "freios"
    | "suspensao"
    | "eletrica"
    | "consumiveis";
  viewProjection: "left" | "right" | "both";
  coords: HotspotCoordinate;
  associatedPartIds: string[];
  calloutVector: {
    labelAnchorX: number;
    labelAnchorY: number;
    elbowX: number;
    elbowY: number;
    strokeColor: "#00F2FE" | "#10B981" | "#EF4444" | "#F59E0B" | "#38BDF8";
  };
  description: string;
  criticalTorque?: string;
  gotchaSummary: string;
}

export const ADV150_ANATOMICAL_HOTSPOTS: ReadonlyArray<MotorcycleAnatomyHotspot> = [
  {
    id: "hotspot-cvt-primario",
    code: "HS-CVT-PRI",
    name: "Variador Primário (Polia Móvel & Virabrequim)",
    subsystem: "cvt",
    viewProjection: "left",
    coords: { x: 41.0, y: 68.0 },
    associatedPartIds: [
      "oem-porca-virabrequim-90201",
      "oem-roletes-22123",
      "oem-face-movel-22110",
      "oem-face-fixa-22102",
      "oem-bucha-primaria-22105",
      "oem-cursores-22011",
      "oem-arruela-belleville-90441",
    ],
    calloutVector: {
      labelAnchorX: 30.0,
      labelAnchorY: 82.0,
      elbowX: 30.0,
      elbowY: 68.0,
      strokeColor: "#00F2FE",
    },
    description:
      "Extremidade estriada esquerda do virabrequim M14 com polia móvel, 6 roletes de 21g e ventilador fixo.",
    criticalTorque: "59 N.m (6,0 kgf.m)",
    gotchaSummary:
      "MONTAGEM 100% A SECO! Proibido graxa nos roletes. Seguir o Protocolo Belt Sinking antes do torque final.",
  },
  {
    id: "hotspot-cvt-secundario",
    code: "HS-CVT-SEC",
    name: "Polia Secundária & Embreagem Centrífuga",
    subsystem: "cvt",
    viewProjection: "left",
    coords: { x: 60.0, y: 66.0 },
    associatedPartIds: [
      "oem-campana-22100",
      "oem-porca-campana-90202",
      "oem-sapatas-22535",
      "oem-contra-mola-23233",
      "oem-porca-mola-90202-187",
      "oem-pinos-torque-cam-23225",
    ],
    calloutVector: {
      labelAnchorX: 70.0,
      labelAnchorY: 82.0,
      elbowX: 70.0,
      elbowY: 66.0,
      strokeColor: "#F59E0B",
    },
    description:
      "Eixo primário do redutor traseiro com embreagem de 3 sapatas, mola de contraste de 50 kgf e campana de 125 mm.",
    criticalTorque: "Campana: 49 N.m | Mola M28: 54 N.m",
    gotchaSummary:
      "Canaletas de torque cam exigem graxa de poliureia MoS2 de alta temperatura. Limite de campana: 125,5 mm.",
  },
  {
    id: "hotspot-cvt-correia",
    code: "HS-CVT-BELT",
    name: "Correia de Transmissão V-Belt (Bando Kevlar)",
    subsystem: "cvt",
    viewProjection: "left",
    coords: { x: 50.5, y: 67.0 },
    associatedPartIds: ["oem-correia-23100"],
    calloutVector: {
      labelAnchorX: 50.5,
      labelAnchorY: 86.0,
      elbowX: 50.5,
      elbowY: 76.0,
      strokeColor: "#EF4444",
    },
    description:
      "Elemento de tração contínua entre as duas polias com cordonéis em Kevlar/aramida.",
    criticalTorque: "Protocolo Belt Sinking",
    gotchaSummary:
      "Largura padrão 19,5 mm; descarte compulsório em 18,5 mm. Deve afundar na secundária antes de apertar a frente.",
  },
  {
    id: "hotspot-redutor-final",
    code: "HS-REDUTOR",
    name: "Transmissão Final (Redutor & Bujões M8)",
    subsystem: "cvt",
    viewProjection: "left",
    coords: { x: 67.5, y: 69.5 },
    associatedPartIds: ["oem-bujao-redutor-90131", "oem-arruela-redutor-90495"],
    calloutVector: {
      labelAnchorX: 82.0,
      labelAnchorY: 78.0,
      elbowX: 82.0,
      elbowY: 69.5,
      strokeColor: "#38BDF8",
    },
    description:
      "Caixa de engrenagens de redução final na balança esquerda com capacidade de 120 ml.",
    criticalTorque: "13 N.m (1,3 kgf.m)",
    gotchaSummary:
      "Substituir sempre as 2 arruelas de alumínio de 8 mm. Abastecer com Pro Honda 10W-30 JASO MB.",
  },
  {
    id: "hotspot-motor-carter",
    code: "HS-MOT-CARTER",
    name: "Cárter do Motor (Bujão M12 & Peneira M30)",
    subsystem: "motor",
    viewProjection: "left",
    coords: { x: 48.0, y: 74.0 },
    associatedPartIds: [
      "oem-bujao-motor-90131",
      "oem-arruela-motor-94109",
      "oem-tela-oleo-15421",
      "oem-mola-tela-15426",
      "oem-tampa-tela-12361",
      "oem-oring-tela-91302",
    ],
    calloutVector: {
      labelAnchorX: 38.0,
      labelAnchorY: 90.0,
      elbowX: 38.0,
      elbowY: 74.0,
      strokeColor: "#10B981",
    },
    description:
      "Fundo do bloco do motor eSP KF43E com bujão de escoamento e tela filtrante de óleo.",
    criticalTorque: "Bujão M12: 24 N.m | Peneira M30: 20 N.m",
    gotchaSummary:
      "Trocar arruela de esmagamento de 12 mm a cada troca (800 ml). Não rosquear a vareta ao medir nível.",
  },
  {
    id: "hotspot-motor-cabecote",
    code: "HS-MOT-HEAD",
    name: "Cabeçote, Válvulas OHC & Vela NGK",
    subsystem: "motor",
    viewProjection: "both",
    coords: { x: 44.0, y: 56.0 },
    associatedPartIds: [
      "oem-tampa-cabecote-12310",
      "oem-junta-cabecote-12391",
      "oem-valvula-adm-14711",
      "oem-valvula-esc-14721",
      "oem-contraporca-valvula-90206",
      "oem-vela-ignicao-31918",
      "oem-sensor-eot-37870",
    ],
    calloutVector: {
      labelAnchorX: 30.0,
      labelAnchorY: 42.0,
      elbowX: 30.0,
      elbowY: 56.0,
      strokeColor: "#10B981",
    },
    description:
      "Topo do motor com balancins roletados, comando acionado por corrente e vela vertical NGK MR8K-9.",
    criticalTorque: "Tampa: 10 N.m | Contraporcas M5: 10 N.m | Vela: 16 N.m",
    gotchaSummary:
      "Regulagem de válvulas OBRIGATORIAMENTE A FRIO (< 35°C): Admissão 0,10 mm e Escape 0,24 mm. Gap da vela 0,80-0,90 mm.",
  },
  {
    id: "hotspot-motor-radiador",
    code: "HS-MOT-RAD",
    name: "Radiador Lateral, Bomba d'Água & Tampa 108 kPa",
    subsystem: "motor",
    viewProjection: "right",
    coords: { x: 52.5, y: 65.0 },
    associatedPartIds: [
      "oem-radiador-19010",
      "oem-tampa-rad-19037",
      "oem-bomba-agua-19200",
      "oem-dreno-coolant-90004",
      "oem-arruela-dreno-coolant-90441",
      "oem-capa-defletora-19150",
    ],
    calloutVector: {
      labelAnchorX: 68.0,
      labelAnchorY: 52.0,
      elbowX: 68.0,
      elbowY: 65.0,
      strokeColor: "#38BDF8",
    },
    description:
      "Sistema de arrefecimento líquido na lateral direita acoplado ao virabrequim com ventilação forçada.",
    criticalTorque: "Dreno bomba M6: 10 N.m | Tampa Defletora: 9 N.m",
    gotchaSummary:
      "Exclusivo Honda Pro Coolant Tipo 2 (OAT 50/50 orgânico sem silicatos). Purga de bolhas ativa de 3 a 5 min.",
  },
  {
    id: "hotspot-pgmfi-tbi",
    code: "HS-PGMFI-TBI",
    name: "Injeção PGM-FI (Corpo TBI Keihin 26mm & Injetor)",
    subsystem: "pgmfi",
    viewProjection: "both",
    coords: { x: 46.5, y: 48.0 },
    associatedPartIds: [
      "oem-corpo-tbi-16400",
      "oem-injetor-16450",
      "oem-valvula-iacv-16430",
      "oem-sensor-unit-16060",
      "oem-sonda-lambda-36532",
    ],
    calloutVector: {
      labelAnchorX: 32.0,
      labelAnchorY: 34.0,
      elbowX: 32.0,
      elbowY: 48.0,
      strokeColor: "#00F2FE",
    },
    description:
      "Conjunto de alimentação com borboleta de aceleração revestida de MoS2 e bico injetor de 6 furos (294 kPa).",
    criticalTorque: "Coletor TBI: 10 N.m | IACV: 2,1 N.m | Sonda: 25 N.m",
    gotchaSummary:
      "PROIBIDO RASPAGEM OU SOLVENTES AGRESSIVOS na borboleta (preservar película de MoS2). Lenta em 1.700 ± 100 rpm.",
  },
  {
    id: "hotspot-filtro-ar",
    code: "HS-FILTRO-AR",
    name: "Caixa do Filtro de Ar Viscoso OEM",
    subsystem: "consumiveis",
    viewProjection: "left",
    coords: { x: 53.0, y: 53.0 },
    associatedPartIds: ["oem-filtro-ar-17210", "oem-filtro-cvt-17274"],
    calloutVector: {
      labelAnchorX: 68.0,
      labelAnchorY: 40.0,
      elbowX: 68.0,
      elbowY: 53.0,
      strokeColor: "#EF4444",
    },
    description:
      "Caixa de admissão plástica com elemento filtrante de papel viscoso embebido em óleo.",
    criticalTorque: "Parafusos tampa caixa: 1,5 N.m",
    gotchaSummary:
      "PROIBIDO AR COMPRIMIDO! O jato rompe os micro-poros e empurra poeira para o cilindro. Troca a 18.000 km.",
  },
  {
    id: "hotspot-freio-diant",
    code: "HS-BRK-FR",
    name: "Freio Dianteiro (Disco Wave 240mm, Cáliper Nissin & ABS)",
    subsystem: "freios",
    viewProjection: "both",
    coords: { x: 18.0, y: 68.5 },
    associatedPartIds: [
      "oem-disco-diant-45251",
      "oem-caliper-diant-45150",
      "oem-pastilhas-diant-06455",
      "oem-sensor-abs-38520",
      "oem-fônica-abs-44515",
      "oem-pino-pastilhas-45215",
    ],
    calloutVector: {
      labelAnchorX: 8.0,
      labelAnchorY: 56.0,
      elbowX: 8.0,
      elbowY: 68.5,
      strokeColor: "#10B981",
    },
    description:
      "Conjunto de frenagem dianteiro com cáliper Nissin de 2 pistões e sensor de velocidade Hall do ABS.",
    criticalTorque: "Cáliper M10: 30 N.m | Disco Wave: 42 N.m | Pino pastilhas: 17 N.m",
    gotchaSummary:
      "Limite de descarte do disco gravado MIN. TH. 3.5 mm. Pastilhas Nissin descarte em 1,0 mm. Trava química média azul.",
  },
  {
    id: "hotspot-freio-tras",
    code: "HS-BRK-RR",
    name: "Freio Traseiro (Disco Wave 220mm & Cáliper Nissin)",
    subsystem: "freios",
    viewProjection: "right",
    coords: { x: 72.0, y: 67.0 },
    associatedPartIds: [
      "oem-disco-tras-43251",
      "oem-caliper-tras-43150",
      "oem-pastilhas-tras-06435",
      "oem-sangria-freio-43352",
    ],
    calloutVector: {
      labelAnchorX: 86.0,
      labelAnchorY: 56.0,
      elbowX: 86.0,
      elbowY: 67.0,
      strokeColor: "#10B981",
    },
    description: "Cáliper traseiro flutuante de pistão simples ancorado na balança de alumínio.",
    criticalTorque: "Cáliper M8: 22 N.m | Válvula purga: 5,4 N.m | Banjo M10: 34 N.m",
    gotchaSummary:
      "Nunca apertar a purga além de 5,4 N.m. Lubrificar pinos deslizantes exclusivamente com graxa de silicone neutra pura.",
  },
  {
    id: "hotspot-susp-diant",
    code: "HS-SUSP-FR",
    name: "Garfo Telescópico Showa 31mm (Curso 130mm)",
    subsystem: "suspensao",
    viewProjection: "both",
    coords: { x: 23.5, y: 45.0 },
    associatedPartIds: [
      "oem-garfo-diant-51400",
      "oem-retentor-garfo-51490",
      "oem-oleo-garfo-08208",
      "oem-parafusos-mesa-95801",
      "oem-eixo-diant-90305",
    ],
    calloutVector: {
      labelAnchorX: 12.0,
      labelAnchorY: 30.0,
      elbowX: 12.0,
      elbowY: 45.0,
      strokeColor: "#38BDF8",
    },
    description: "Suspensão dianteira de longo curso (130 mm) Showa com tubos cromados de 31 mm.",
    criticalTorque: "Mesa Showa M10: 27 N.m | Eixo dianteiro M12: 59 N.m",
    gotchaSummary:
      "Capacidade ~115 ml Honda Fork Oil 10W por bengala. Aperto em cruz alternado na mesa para evitar empenamento.",
  },
  {
    id: "hotspot-susp-tras",
    code: "HS-SUSP-RR",
    name: "Amortecedores Traseiros Duplos Showa Piggyback a Gás",
    subsystem: "suspensao",
    viewProjection: "both",
    coords: { x: 64.5, y: 49.0 },
    associatedPartIds: [
      "oem-amortecedor-esq-52400",
      "oem-amortecedor-dir-52500",
      "oem-parafuso-sup-amort-90103",
      "oem-parafuso-inf-amort-90104",
      "oem-eixo-tras-90305",
    ],
    calloutVector: {
      labelAnchorX: 80.0,
      labelAnchorY: 34.0,
      elbowX: 80.0,
      elbowY: 49.0,
      strokeColor: "#F59E0B",
    },
    description: "Amortecedores duplos com subtanque pressurizado a nitrogênio e curso de 120 mm.",
    criticalTorque: "Superior M10: 40 N.m | Inferior M8: 27 N.m | Eixo traseiro M16: 118 N.m",
    gotchaSummary:
      "Subtanque selado de fábrica; proibido perfurar ou recarregar. Porca M16 do eixo traseiro requer 118 N.m e contrapino novo.",
  },
  {
    id: "hotspot-eletrica-bateria",
    code: "HS-ELE-BAT",
    name: "Bateria Selada VRLA YTZ6V & Smart Key",
    subsystem: "eletrica",
    viewProjection: "both",
    coords: { x: 50.0, y: 41.5 },
    associatedPartIds: [
      "oem-bateria-31500",
      "oem-smartkey-fob-35111",
      "oem-bateria-cr2032",
      "oem-estator-acg-31120",
    ],
    calloutVector: {
      labelAnchorX: 38.0,
      labelAnchorY: 24.0,
      elbowX: 38.0,
      elbowY: 41.5,
      strokeColor: "#F59E0B",
    },
    description:
      "Compartimento central elétrico sob o banco com bateria de 12V 5Ah e receptor da chave presencial.",
    criticalTorque: "Bornes: aperto manual firme",
    gotchaSummary:
      "Tensão de repouso ≥ 12,8V. Na partida ACG nunca pode cair abaixo de 10,2V. Bateria da Smart Key: CR2032 com polo (+) para baixo.",
  },
  {
    id: "hotspot-eletrica-dlc",
    code: "HS-ELE-DLC",
    name: "Conector de Diagnóstico DLC Vermelho (Reset ECM)",
    subsystem: "eletrica",
    viewProjection: "both",
    coords: { x: 54.5, y: 39.0 },
    associatedPartIds: ["oem-jumper-scs-070pz", "oem-ecm-38770"],
    calloutVector: {
      labelAnchorX: 68.0,
      labelAnchorY: 20.0,
      elbowX: 68.0,
      elbowY: 39.0,
      strokeColor: "#EF4444",
    },
    description: "Tomada de serviço de 4 vias vermelha sob o assento para jumper SCS oficial.",
    criticalTorque: "Encaixe mecânico",
    gotchaSummary:
      "Conectar estritamente pino Marrom (SCS) com Verde/Preto (Terra). PROIBIDO TOCAR NO PINO 4 (+12V) sob risco de queimar o ECM.",
  },
];
